/**
 * TemplateEditScreen Component
 * 
 * Full-page editor for customizing template styles.
 * Left panel: Style configuration (Trait & Attribute styles)
 * Right panel: Full-size live preview
 */

import React, { useState, useCallback, useMemo } from 'react';
import { ArrowLeft, Save, RotateCcw, Loader2, Edit2 } from 'lucide-react';
import { DeviceModalConfig, TraitStyleConfig, AttributeStyleConfig, TemplateStyleId, TraitStyleId } from '../deviceModals/types';
import { DeviceModalPreview } from './DeviceModalPreview';
import { StyleSelector, StyleSelectorGroup } from './StyleSelector';
import { DraggableStyleList, DraggableItem } from './DraggableStyleList';
import {
  getDeviceTypeTraits,
  getDeviceTypeAttributes,
  getAvailableTraitStyles,
  getAvailableAttributeStyles,
  getAvailableIconStyles,
  getDefaultConfig,
  DEVICE_TYPE_DISPLAY_NAMES,
  TraitName,
  AttributeName
} from '../../services/deviceModalThemeService';
import { useLanguage } from '../../contexts/LanguageContext';

export interface TemplateEditScreenProps {
  /** Device type being edited */
  deviceType: string;
  /** Template ID being edited */
  templateId: string;
  /** Initial configuration */
  initialConfig: DeviceModalConfig;
  /** Called when Save & Apply is clicked (saves and applies as current template) */
  onSave: (config: DeviceModalConfig) => Promise<void>;
  /** Called when Save is clicked (saves without applying) */
  onSaveOnly?: (config: DeviceModalConfig) => Promise<void>;
  /** Called when Save as... is clicked (save with new name) */
  onSaveAs?: (config: DeviceModalConfig, newName: string) => Promise<void>;
  /** Called when Back is clicked */
  onBack: () => void;
  /** Whether save is in progress */
  isSaving?: boolean;
}

export const TemplateEditScreen: React.FC<TemplateEditScreenProps> = ({
  deviceType,
  templateId,
  initialConfig,
  onSave,
  onSaveOnly,
  onSaveAs,
  onBack,
  isSaving = false
}) => {
  const { language } = useLanguage();
  
  // State for current configuration
  const [config, setConfig] = useState<DeviceModalConfig>(initialConfig);
  
  // State for Save As dialog
  const [showSaveAsDialog, setShowSaveAsDialog] = useState(false);
  const [saveAsName, setSaveAsName] = useState('');
  
  // Track if there are unsaved changes
  const hasChanges = useMemo(() => {
    return JSON.stringify(config) !== JSON.stringify(initialConfig);
  }, [config, initialConfig]);

  // Get traits, attributes, and icon styles for this device type
  const allTraits = getDeviceTypeTraits(deviceType);
  const attributes = getDeviceTypeAttributes(deviceType);
  const iconStyles = getAvailableIconStyles(deviceType);

  // Get draggable attributes (exclude StatusIndicator)
  const draggableAttributeNames = useMemo(() => {
    return attributes.filter(attr => attr !== 'StatusIndicator');
  }, [attributes]);

  // State for component order (Attributes + Traits, can be reordered by user)
  const [traitOrder, setTraitOrder] = useState<string[]>(() => {
    // Use saved order from config, or default order (Attributes first, then Traits)
    if (config.traitOrder) {
      return config.traitOrder;
    }
    return [...draggableAttributeNames, ...allTraits];
  });

  // Ordered traits based on user's order
  const traits = useMemo(() => {
    // Filter to only include traits that exist for this device type
    const validTraits = traitOrder.filter(t => allTraits.includes(t as any));
    // Add any new traits that aren't in the saved order
    const newTraits = allTraits.filter(t => !traitOrder.includes(t));
    return [...validTraits, ...newTraits] as typeof allTraits;
  }, [traitOrder, allTraits]);

  // ✅ Sync new traits/attributes to config when they're detected
  // This ensures new components (like ThermostatMode) are added to the preview
  React.useEffect(() => {
    const allComponents = [...draggableAttributeNames, ...allTraits];
    const newComponents = allComponents.filter(c => !traitOrder.includes(c));
    
    if (newComponents.length > 0) {
      const updatedOrder = [...traitOrder, ...newComponents];
      setTraitOrder(updatedOrder);
      setConfig(prev => ({
        ...prev,
        traitOrder: updatedOrder
      }));
    }
  }, [allTraits, draggableAttributeNames]); // Only run when traits/attributes change

  // Get display name for device type
  const getDeviceTypeDisplayName = (type: string): string => {
    const names = DEVICE_TYPE_DISPLAY_NAMES[type];
    if (names) {
      return language === 'zh-TW' ? names.zh : names.en;
    }
    return type;
  };

  // Handle trait style change
  const handleTraitStyleChange = useCallback((trait: TraitName, styleId: string) => {
    setConfig(prev => ({
      ...prev,
      traitStyles: {
        ...prev.traitStyles,
        [trait]: styleId as TraitStyleId
      } as TraitStyleConfig
    }));
  }, []);

  // Handle attribute style change
  const handleAttributeStyleChange = useCallback((attribute: AttributeName, styleId: string) => {
    setConfig(prev => ({
      ...prev,
      attributeStyles: {
        ...prev.attributeStyles,
        [attribute]: styleId as TraitStyleId
      } as AttributeStyleConfig
    }));
  }, []);

  // Handle icon style change
  const handleIconStyleChange = useCallback((styleId: string) => {
    setConfig(prev => ({
      ...prev,
      iconStyle: styleId as TraitStyleId
    }));
  }, []);

  // Handle trait order change (drag and drop)
  const handleTraitOrderChange = useCallback((newOrder: string[]) => {
    setTraitOrder(newOrder);
    setConfig(prev => ({
      ...prev,
      traitOrder: newOrder
    }));
  }, []);

  // Handle component visibility change
  const handleVisibilityChange = useCallback((componentId: string, visible: boolean) => {
    setConfig(prev => {
      const hiddenComponents = prev.hiddenComponents || [];
      if (visible) {
        // Remove from hidden list
        return {
          ...prev,
          hiddenComponents: hiddenComponents.filter(id => id !== componentId)
        };
      } else {
        // Add to hidden list
        return {
          ...prev,
          hiddenComponents: [...hiddenComponents, componentId]
        };
      }
    });
  }, []);

  // Handle reset to default
  const handleReset = useCallback(() => {
    // Get template-specific default config
    const defaultConfig = getDefaultConfig(deviceType, templateId);
    setConfig(defaultConfig);
    // Also reset trait order
    setTraitOrder([...draggableAttributeNames, ...allTraits]);
  }, [deviceType, templateId, draggableAttributeNames, allTraits]);

  // Handle save
  const handleSave = useCallback(async () => {
    await onSave(config);
  }, [config, onSave]);

  // Handle Save Only (save without applying)
  const handleSaveOnly = useCallback(async () => {
    if (onSaveOnly) {
      await onSaveOnly(config);
    }
  }, [config, onSaveOnly]);

  // Handle Save As - open dialog
  const handleOpenSaveAs = useCallback(() => {
    setSaveAsName(config.templateName || templateId);
    setShowSaveAsDialog(true);
  }, [config.templateName, templateId]);

  // Handle Save As - confirm save
  const handleConfirmSaveAs = useCallback(async () => {
    if (onSaveAs && saveAsName.trim()) {
      const newConfig = {
        ...config,
        templateName: saveAsName.trim()
      };
      await onSaveAs(newConfig, saveAsName.trim());
      setShowSaveAsDialog(false);
    }
  }, [config, saveAsName, onSaveAs]);

  // Trait display names
  const traitDisplayNames: Record<string, { zh: string; en: string }> = {
    OnOff: { zh: '開關', en: 'On/Off' },
    Brightness: { zh: '亮度', en: 'Brightness' },
    ColorSetting: { zh: '色溫', en: 'Color Temperature' },
    FanSpeed: { zh: '風速', en: 'Fan Speed' },
    Mode: { zh: '模式', en: 'Mode' },
    Timer: { zh: '計時器', en: 'Timer' },
    CustomToggles: { zh: '自訂開關', en: 'Custom Toggles' },
    HumiditySetting: { zh: '濕度設定', en: 'Humidity Setting' },
    TemperatureSetting: { zh: '溫度設定', en: 'Temperature Setting' },
    ThermostatMode: { zh: '空調模式', en: 'Thermostat Mode' },
    OpenClose: { zh: '開合', en: 'Open/Close' }
  };

  // Attribute display names
  const attributeDisplayNames: Record<string, { zh: string; en: string }> = {
    SensorDisplay: { zh: '感應器顯示', en: 'Sensor Display' },
    SensorChart: { zh: '感應器圖表', en: 'Sensor Chart' },
    StatusIndicator: { zh: '狀態指示器', en: 'Status Indicator' },
    OccupancyTimeline: { zh: '佔用時間軸', en: 'Occupancy Timeline' },
    AQIDisplay: { zh: '空氣品質顯示', en: 'AQI Display' }
  };

  const getTraitDisplayName = (trait: string): string => {
    const names = traitDisplayNames[trait];
    if (names) {
      return language === 'zh-TW' ? names.zh : names.en;
    }
    return trait;
  };

  const getAttributeDisplayName = (attribute: string): string => {
    const names = attributeDisplayNames[attribute];
    if (names) {
      return language === 'zh-TW' ? names.zh : names.en;
    }
    return attribute;
  };

  return (
    <div className="h-full flex flex-col bg-slate-900/30">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 border-b border-slate-700/50 bg-slate-800/30">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-lg font-semibold text-white">
              Editing: {getDeviceTypeDisplayName(deviceType)} - {templateId}
            </h2>
            <p className="text-sm text-slate-400">
              Customize the styles for this template
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleReset}
            disabled={isSaving}
            className="px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700/50 rounded-lg transition-colors flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            {language === 'zh-TW' ? '恢復預設值' : 'Reset Default'}
          </button>
          {onSaveAs && (
            <button
              onClick={handleOpenSaveAs}
              disabled={isSaving}
              className="px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700/50 rounded-lg transition-colors border border-slate-600"
            >
              {language === 'zh-TW' ? '另存新檔' : 'Save as...'}
            </button>
          )}
          {onSaveOnly && (
            <button
              onClick={handleSaveOnly}
              disabled={isSaving || !hasChanges}
              className={`
                px-4 py-2 text-sm rounded-lg transition-colors flex items-center gap-2 border
                ${hasChanges 
                  ? 'border-blue-500 text-blue-400 hover:bg-blue-500/10' 
                  : 'border-slate-600 text-slate-400 cursor-not-allowed'
                }
              `}
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {language === 'zh-TW' ? '儲存' : 'Save'}
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
            className={`
              px-4 py-2 text-sm rounded-lg transition-colors flex items-center gap-2
              ${hasChanges 
                ? 'bg-blue-600 text-white hover:bg-blue-700' 
                : 'bg-slate-700 text-slate-400 cursor-not-allowed'
              }
            `}
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {language === 'zh-TW' ? '儲存並套用' : 'Save & Apply'}
          </button>
        </div>
      </header>

      {/* Save As Dialog */}
      {showSaveAsDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-slate-800 rounded-xl border border-slate-700 shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-700">
              <h3 className="text-lg font-semibold text-white">
                {language === 'zh-TW' ? '另存新檔' : 'Save As New Template'}
              </h3>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                {language === 'zh-TW' ? '新樣式名稱' : 'New Template Name'}
              </label>
              <input
                type="text"
                value={saveAsName}
                onChange={(e) => setSaveAsName(e.target.value)}
                placeholder={language === 'zh-TW' ? '輸入樣式名稱...' : 'Enter template name...'}
                className="w-full px-4 py-2.5 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && saveAsName.trim()) {
                    handleConfirmSaveAs();
                  }
                  if (e.key === 'Escape') {
                    setShowSaveAsDialog(false);
                  }
                }}
              />
              <p className="mt-2 text-xs text-slate-500">
                {language === 'zh-TW' 
                  ? '將會建立一個新的樣式設定，不會覆蓋現有的設定。' 
                  : 'This will create a new template configuration without overwriting the existing one.'}
              </p>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-700 bg-slate-800/50">
              <button
                onClick={() => setShowSaveAsDialog(false)}
                className="px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700/50 rounded-lg transition-colors"
              >
                {language === 'zh-TW' ? '取消' : 'Cancel'}
              </button>
              <button
                onClick={handleConfirmSaveAs}
                disabled={!saveAsName.trim() || isSaving}
                className={`px-4 py-2 text-sm rounded-lg transition-colors flex items-center gap-2
                  ${saveAsName.trim() 
                    ? 'bg-blue-600 text-white hover:bg-blue-700' 
                    : 'bg-slate-700 text-slate-400 cursor-not-allowed'}
                `}
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {language === 'zh-TW' ? '儲存' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Style Configuration */}
        <div className="w-80 flex-shrink-0 overflow-y-auto border-r border-slate-700/50 bg-slate-800/20 p-4">
          <h3 className="text-sm font-semibold text-white mb-4">Style Configuration</h3>

          {/* Template Name (editable) */}
          <div className="mb-4 p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
            <label className="text-xs font-medium text-slate-400 mb-1.5 block">
              {language === 'zh-TW' ? '樣式名稱' : 'Template Name'}
            </label>
            <div className="relative">
              <input
                type="text"
                value={config.templateName || templateId}
                onChange={(e) => setConfig(prev => ({
                  ...prev,
                  templateName: e.target.value || undefined
                }))}
                placeholder={templateId}
                disabled={isSaving}
                className="w-full px-3 py-2 pr-8 bg-slate-900/50 border border-slate-600/50 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-colors disabled:opacity-50"
              />
              <Edit2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
            </div>
            <p className="text-xs text-slate-500 mt-1.5">
              {language === 'zh-TW' ? `內部 ID: ${templateId}` : `Internal ID: ${templateId}`}
            </p>
          </div>

          {/* === 統一區塊順序（固定 → 可拖拉）=== */}

          {/* 固定區塊 1: StatusIndicator (狀態指示器) */}
          {attributes.includes('StatusIndicator') && (() => {
            const styles = getAvailableAttributeStyles('StatusIndicator');
            const currentStyle = config.attributeStyles?.['StatusIndicator' as keyof AttributeStyleConfig] || 'Style01';
            return (
              <StyleSelectorGroup 
                title={language === 'zh-TW' ? '狀態指示器' : 'Status Indicator'}
                collapsible={true}
                defaultCollapsed={false}
              >
                <StyleSelector
                  label={getAttributeDisplayName('StatusIndicator')}
                  options={styles}
                  value={currentStyle}
                  onChange={(styleId) => handleAttributeStyleChange('StatusIndicator', styleId)}
                  disabled={isSaving}
                />
              </StyleSelectorGroup>
            );
          })()}

          {/* 固定區塊 2: Icon Styles (設備圖標 - 小圖標 + 大圖標) */}
          {iconStyles.length > 0 && (
            <StyleSelectorGroup 
              title={language === 'zh-TW' ? '設備圖標' : 'Device Icon'}
              collapsible={true}
              defaultCollapsed={false}
            >
              <StyleSelector
                label={language === 'zh-TW' ? '小圖標（標題列）' : 'Small Icon (Title Bar)'}
                options={iconStyles}
                value={config.smallIconStyle || config.iconStyle || 'Style01'}
                onChange={(styleId) => {
                  setConfig(prev => ({
                    ...prev,
                    smallIconStyle: styleId as TraitStyleId
                  }));
                }}
                disabled={isSaving}
              />
              <div className="mt-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-300">
                    {language === 'zh-TW' ? '大圖標（畫面中央）' : 'Big Icon (Content Area)'}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleVisibilityChange('BigIcon', (config.hiddenComponents || []).includes('BigIcon'))}
                    className={`p-1.5 rounded-lg transition-colors ${
                      !(config.hiddenComponents || []).includes('BigIcon')
                        ? 'text-blue-400 hover:bg-blue-500/20' 
                        : 'text-slate-500 hover:bg-slate-700/50'
                    }`}
                    title={!(config.hiddenComponents || []).includes('BigIcon') ? '隱藏' : '顯示'}
                  >
                    {!(config.hiddenComponents || []).includes('BigIcon') 
                      ? <span className="material-symbols-outlined text-base">visibility</span>
                      : <span className="material-symbols-outlined text-base">visibility_off</span>
                    }
                  </button>
                </div>
                <StyleSelector
                  label=""
                  options={iconStyles}
                  value={config.bigIconStyle || config.iconStyle || 'Style01'}
                  onChange={(styleId) => {
                    setConfig(prev => ({
                      ...prev,
                      bigIconStyle: styleId as TraitStyleId
                    }));
                  }}
                  disabled={isSaving || (config.hiddenComponents || []).includes('BigIcon')}
                />
              </div>
            </StyleSelectorGroup>
          )}

          {/* 可拖拉區塊: Attributes (除 StatusIndicator) + Traits */}
          {(() => {
            // 收集可拖拉的項目：其他 Attributes + 所有 Traits
            const hiddenComponents = config.hiddenComponents || [];
            
            // 創建所有可用組件的 map
            const allComponentsMap: Record<string, {
              id: string;
              label: string;
              options: { id: string; name: string; description?: string }[];
              value: string;
              onChange: (value: string) => void;
              visible: boolean;
              onVisibilityChange: (visible: boolean) => void;
            }> = {};

            // 添加可拖拉的 Attributes
            draggableAttributeNames.forEach(attr => {
              const styles = getAvailableAttributeStyles(attr);
              const currentStyle = config.attributeStyles?.[attr as keyof AttributeStyleConfig] || 'Style01';
              const componentId = attr;
              allComponentsMap[attr] = {
                id: `attr_${attr}`,
                label: getAttributeDisplayName(attr),
                options: styles,
                value: currentStyle,
                onChange: (styleId: string) => handleAttributeStyleChange(attr, styleId),
                visible: !hiddenComponents.includes(componentId),
                onVisibilityChange: (visible: boolean) => handleVisibilityChange(componentId, visible)
              };
            });

            // 添加所有 Traits
            allTraits.forEach(trait => {
              const styles = getAvailableTraitStyles(trait);
              const currentStyle = config.traitStyles?.[trait as keyof TraitStyleConfig] || 'Style01';
              const componentId = trait;
              allComponentsMap[trait] = {
                id: `trait_${trait}`,
                label: getTraitDisplayName(trait),
                options: styles,
                value: currentStyle,
                onChange: (styleId: string) => handleTraitStyleChange(trait, styleId),
                visible: !hiddenComponents.includes(componentId),
                onVisibilityChange: (visible: boolean) => handleVisibilityChange(componentId, visible)
              };
            });

            // 根據 traitOrder 排序，然後添加不在 order 中的項目
            const orderedItems: typeof allComponentsMap[string][] = [];
            traitOrder.forEach(name => {
              if (allComponentsMap[name]) {
                orderedItems.push(allComponentsMap[name]);
                delete allComponentsMap[name];
              }
            });
            // 添加剩餘的項目
            Object.values(allComponentsMap).forEach(item => orderedItems.push(item));
            
            const draggableItems = orderedItems;

            if (draggableItems.length === 0) return null;

            return (
              <StyleSelectorGroup 
                title={language === 'zh-TW' ? '元件樣式' : 'Component Styles'}
                collapsible={true}
                defaultCollapsed={false}
              >
                <p className="text-xs text-slate-500 mb-2">
                  {language === 'zh-TW' ? '可拖動調整順序，點擊眼睛切換顯示/隱藏' : 'Drag to reorder, click eye to show/hide'}
                </p>
                <DraggableStyleList
                  items={draggableItems}
                  onReorder={(newOrder) => {
                    // 提取完整的組件順序（Attributes + Traits）
                    const newComponentOrder = newOrder.map(id => {
                      if (id.startsWith('attr_')) return id.replace('attr_', '');
                      if (id.startsWith('trait_')) return id.replace('trait_', '');
                      return id;
                    });
                    handleTraitOrderChange(newComponentOrder);
                  }}
                  disabled={isSaving}
                  showVisibilityToggle={true}
                />
              </StyleSelectorGroup>
            );
          })()}

          {/* Empty State */}
          {traits.length === 0 && attributes.length === 0 && (
            <div className="text-center py-8 text-slate-500">
              <p className="text-sm">No configurable styles for this template.</p>
            </div>
          )}
        </div>

        {/* Right Panel - Live Preview (100% size, scrollable if needed) */}
        <div className="flex-1 flex flex-col bg-slate-900/50 overflow-y-auto relative">
          <h3 className="sticky top-0 text-sm font-semibold text-white z-10 py-3 bg-slate-900/90 backdrop-blur-sm text-center border-b border-slate-700/30">
            Live Preview
          </h3>
          <div className="flex-1 flex items-start justify-center p-6">
            <DeviceModalPreview
              deviceType={deviceType}
              config={config}
              scale={1}
              showContainer={true}
              className="shadow-2xl"
              interactive={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateEditScreen;
